import React, { useCallback, useEffect, useState } from "react";
import { Button, Card, Descriptions, Modal, Space, Spin, Typography, message } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import {
  LeanCustomsService,
  type LeanCustomsDeclaration,
} from "../../services/leancustomsService";
import { formatLeanCustomsOrderDetailLine } from "../../utils/leanCustomsOrderDisplay";
import {
  declarationBelongsToOrder,
  pickDeclarationsForLinkedOrder,
} from "../../utils/leanCustomsDeclarationLookup";
import { searchMatchingLeanCustomsDeclarations } from "../../services/leanCustomsBookingSearch";
import {
  buildLeanCustomsOrderDetailsDisplay,
  type LeanCustomsOrderDetailsDisplay,
} from "../../utils/leanCustomsOrderDetails";
import { sortDeclarationDocumentsUtbFirst } from "../../utils/leanCustomsDeclarationDocumentsSort";
import { sortMessagesNewestFirst } from "../../utils/leanCustomsMessageDisplay";
import { supabase } from "../../lib/supabase";
import { BookingCustomsLinkedOrderPanel } from "./BookingCustomsLinkedOrderPanel";
import {
  BookingCustomsLinkedDeclarationPanel,
  type DeclarationEnrichment,
} from "./BookingCustomsLinkedDeclarationPanel";

export interface BookingCustomsLinkedSummaryProps {
  orderId?: string | null;
  bookingId: number;
  bookingReference: string;
  companyId: number;
  organizationId: string;
  savedDeclarationId?: string | null;
  refreshTrigger?: number;
  onRefresh?: () => void;
  onRecheck?: () => void;
}

async function loadDeclarationEnrichment(
  declarationId: string,
  companyId: number
): Promise<DeclarationEnrichment> {
  const [invRes, docRes, itemsRes, msgRes] = await Promise.all([
    LeanCustomsService.listDeclarationInvoices(declarationId, companyId),
    LeanCustomsService.listDeclarationDocuments(declarationId, companyId),
    LeanCustomsService.listDeclarationItems(declarationId, companyId),
    LeanCustomsService.listDeclarationMessages(declarationId, companyId),
  ]);

  const items = (itemsRes.success && itemsRes.data?.results
    ? itemsRes.data.results
    : []
  ).filter((item) => {
    const itemDeclarationId = item.declaration?.id || item.declaration_id;
    return itemDeclarationId === declarationId;
  });

  return {
    invoices: invRes.success && invRes.data?.results ? invRes.data.results : [],
    documents:
      docRes.success && docRes.data?.results
        ? sortDeclarationDocumentsUtbFirst(docRes.data.results)
        : [],
    itemCount: items.length,
    messages:
      msgRes.success && msgRes.data?.results
        ? sortMessagesNewestFirst(msgRes.data.results)
        : [],
  };
}

export const BookingCustomsLinkedSummary: React.FC<
  BookingCustomsLinkedSummaryProps
> = ({
  orderId,
  bookingId,
  bookingReference,
  companyId,
  organizationId,
  savedDeclarationId,
  refreshTrigger = 0,
  onRefresh,
  onRecheck,
}) => {
  const [loading, setLoading] = useState(true);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [orderDetailLine, setOrderDetailLine] = useState<string | null>(null);
  const [orderUpdatedAt, setOrderUpdatedAt] = useState<string | null>(null);
  const [orderDetails, setOrderDetails] = useState<LeanCustomsOrderDetailsDisplay | null>(
    null
  );
  const [declarations, setDeclarations] = useState<LeanCustomsDeclaration[]>([]);
  const [declarationNote, setDeclarationNote] = useState<string | null>(null);
  const [declarationEnrichment, setDeclarationEnrichment] = useState<
    Record<string, DeclarationEnrichment>
  >({});
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  const [linkingDeclarationId, setLinkingDeclarationId] = useState<string | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setOrderError(null);
    setDeclarationNote(null);
    setOrderDetails(null);
    setDeclarationEnrichment({});

    (async () => {
      const linkedOrderId = orderId?.trim() || null;
      let orderRes: Awaited<ReturnType<typeof LeanCustomsService.getOrder>> | null =
        null;
      let shipmentsRes: Awaited<
        ReturnType<typeof LeanCustomsService.listOrderShipments>
      > | null = null;

      if (linkedOrderId) {
        [orderRes, shipmentsRes] = await Promise.all([
          LeanCustomsService.getOrder(linkedOrderId, companyId),
          LeanCustomsService.listOrderShipments(linkedOrderId, companyId),
        ]);
        if (cancelled) return;

        if (!orderRes.success || !orderRes.data) {
          setOrderDetailLine(null);
          setOrderUpdatedAt(null);
          setOrderError(orderRes.error ?? "Could not load order from Lean Customs.");
        } else {
          setOrderError(null);
          setOrderDetailLine(formatLeanCustomsOrderDetailLine(orderRes.data));
          const updated = orderRes.data.updated_at ?? orderRes.data.created_at;
          setOrderUpdatedAt(typeof updated === "string" ? updated : null);
          setOrderDetails(
            buildLeanCustomsOrderDetailsDisplay(
              orderRes.data,
              shipmentsRes?.success ? shipmentsRes.data : undefined
            )
          );
        }
      } else {
        setOrderDetailLine(null);
        setOrderUpdatedAt(null);
        setOrderError(null);
        setOrderDetails(null);
      }

      const candidates = await searchMatchingLeanCustomsDeclarations(
        organizationId,
        companyId,
        bookingId,
        bookingReference,
        savedDeclarationId,
        linkedOrderId
      );
      const picked = linkedOrderId
        ? pickDeclarationsForLinkedOrder(candidates, linkedOrderId, bookingId)
        : candidates;

      if (cancelled) return;

      setDeclarations(picked);

      if (picked.length === 0) {
        setDeclarationNote(
          linkedOrderId
            ? "No declaration found for this order yet. Create or link one in Lean Customs."
            : "No declaration found for this booking reference in Lean Customs."
        );
      } else if (
        linkedOrderId &&
        picked.some((d) => !declarationBelongsToOrder(d, linkedOrderId))
      ) {
        setDeclarationNote(
          "Matched by booking reference — verify the declaration belongs to this order in Lean Customs."
        );
      } else {
        setDeclarationNote(null);
      }

      if (!linkedOrderId && picked.length === 0 && !savedDeclarationId) {
        setLoading(false);
        return;
      }

      if (picked.length > 0) {
        setEnrichmentLoading(true);
        const entries = await Promise.all(
          picked.map(async (decl) => {
            try {
              const enrichment = await loadDeclarationEnrichment(decl.id, companyId);
              return [decl.id, enrichment] as const;
            } catch {
              return [
                decl.id,
                {
                  invoices: [],
                  documents: [],
                  itemCount: 0,
                  messages: [],
                },
              ] as const;
            }
          })
        );
        if (!cancelled) {
          setDeclarationEnrichment(Object.fromEntries(entries));
          setEnrichmentLoading(false);
        }
      }
    })()
      .catch(() => {
        if (!cancelled) {
          setOrderError("Could not load Lean Customs data.");
          setDeclarations([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    orderId,
    bookingId,
    bookingReference,
    companyId,
    organizationId,
    savedDeclarationId,
    refreshTrigger,
  ]);

  const handleLinkDeclaration = useCallback(
    (declarationId: string) => {
      Modal.confirm({
        title: "Link declaration to this booking?",
        content: (
          <Typography.Paragraph style={{ marginBottom: 0 }}>
            Save declaration{" "}
            <Typography.Text code>{declarationId}</Typography.Text> on booking{" "}
            <Typography.Text code>EFF{bookingId}</Typography.Text>? Customers will
            see declaration documents on the booking view when linked.
          </Typography.Paragraph>
        ),
        okText: "Link to booking",
        cancelText: "Cancel",
        onOk: async () => {
          setLinkingDeclarationId(declarationId);
          try {
            const { error } = await supabase
              .from("bookings")
              .update({ leancustoms_declaration_id: declarationId })
              .eq("id", bookingId);
            if (error) {
              message.error(error.message || "Failed to update booking");
              return;
            }
            message.success("Declaration linked to this booking.");
            onRefresh?.();
          } finally {
            setLinkingDeclarationId(null);
          }
        },
      });
    },
    [bookingId, onRefresh]
  );

  const missingDeclaration =
    !loading && declarations.length === 0 && !savedDeclarationId;
  const showRecheck = Boolean(onRecheck) && (missingDeclaration || Boolean(orderError));

  return (
    <Card
      size="small"
      title="📋 Linked to this booking"
      style={{ marginBottom: 16 }}
      extra={
        showRecheck ? (
          <Button
            size="small"
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={onRecheck}
          >
            Re-check
          </Button>
        ) : null
      }
    >
      {loading ? (
        <Spin size="small" />
      ) : (
        <Descriptions size="small" column={1} bordered>
          {orderId ? (
            <Descriptions.Item label="Order">
              <BookingCustomsLinkedOrderPanel
                orderId={orderId}
                orderError={orderError}
                orderDetailLine={orderDetailLine}
                orderUpdatedAt={orderUpdatedAt}
                details={orderDetails}
              />
            </Descriptions.Item>
          ) : null}

          <Descriptions.Item label="Declaration">
            {declarations.length === 0 ? (
              <Space direction="vertical" size={4}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {declarationNote ?? "No declaration linked."}
                </Typography.Text>
                {missingDeclaration && onRecheck ? (
                  <Button
                    type="link"
                    size="small"
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={onRecheck}
                    style={{ padding: 0, height: "auto" }}
                  >
                    Re-check Lean Customs
                  </Button>
                ) : null}
              </Space>
            ) : (
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                {declarationNote ? (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {declarationNote}
                  </Typography.Text>
                ) : null}
                {declarations.map((decl) => (
                  <BookingCustomsLinkedDeclarationPanel
                    key={decl.id}
                    declaration={decl}
                    companyId={companyId}
                    savedDeclarationId={savedDeclarationId}
                    linkingDeclarationId={linkingDeclarationId}
                    enrichment={declarationEnrichment[decl.id] ?? null}
                    enrichmentLoading={enrichmentLoading}
                    onLink={handleLinkDeclaration}
                  />
                ))}
              </Space>
            )}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Card>
  );
};
